import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowRight, Sparkles, Heart, Lock } from 'lucide-react';
import { setLeadSource } from '@/lib/leadSource';

/**
 * HomeWhy — Section 3 / Home V2.
 *
 * 3 reasons to choose Prime Vision, sober and authoritative.
 * No KPI numbers; just clear positioning statements.
 */
const COPY = {
  it: {
    eyebrow: 'Perché Prime Vision',
    title_a: 'Tre motivi concreti',
    title_b: 'per cui vale la pena guardarci.',
    reasons: [
      {
        icon: Heart,
        title: 'Costruito da chi visita davvero',
        body: 'Ogni flusso nasce dentro uno studio oculistico reale, non in una sala riunioni. È software che capisce come lavori — perché chi lo ha pensato lo ha vissuto.',
      },
      {
        icon: Sparkles,
        title: 'Un unico software, non sette',
        body: 'Cartella clinica, AI vocale, agenda, fatture, follow-up e documentazione FSE 2.0 in un unico flusso. Niente più rincorse tra programmi diversi.',
      },
      {
        icon: Lock,
        title: 'Dati clinici protetti, GDPR-grade',
        body: 'Crittografia AES-256, server europei, audit trail completo, ruoli e permessi granulari. I tuoi dati restano tuoi.',
      },
    ],
    ctaPrimary: 'Prenota una demo gratuita',
    ctaSecondary: 'Vedi il confronto completo',
  },
  en: {
    eyebrow: 'Why Prime Vision',
    title_a: 'Three concrete reasons',
    title_b: 'why it\'s worth a closer look.',
    reasons: [
      {
        icon: Heart,
        title: 'Built by people who actually examine patients',
        body: 'Every flow is born inside a real eye-care practice, not in a boardroom. It\'s software that understands how you work — because the people who designed it lived it.',
      },
      {
        icon: Sparkles,
        title: 'One software, not seven',
        body: 'Medical records, voice AI, agenda, billing, follow-up and FSE 2.0 documentation in a single flow. No more chasing across different programs.',
      },
      {
        icon: Lock,
        title: 'Clinical data protected, GDPR-grade',
        body: 'AES-256 encryption, European servers, full audit trail, granular roles and permissions. Your data stays yours.',
      },
    ],
    ctaPrimary: 'Book a free demo',
    ctaSecondary: 'See the full comparison',
  },
};

export const HomeWhy = () => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;

  return (
    <section
      data-testid="pv-home-why"
      className="relative bg-white py-20 md:py-28 border-t border-slate-100"
    >
      <div className="pv-container">
        <div className="max-w-3xl">
          <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {c.eyebrow}
          </span>
          <h2 className="mt-5 font-heading font-bold tracking-normal text-[30px] sm:text-[38px] md:text-[44px] leading-[1.10] text-pv-ink">
            {c.title_a}
            <br />
            <span className="text-pv-teal">{c.title_b}</span>
          </h2>
        </div>

        <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {c.reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <article
                key={i}
                data-testid={`pv-home-why-reason-${i + 1}`}
                className="relative bg-white rounded-2xl border border-slate-200 p-7 md:p-8 hover:border-pv-teal/35 transition-colors"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-pv-ice border border-pv-teal/20">
                  <Icon className="w-5 h-5 text-pv-teal" />
                </span>
                <h3 className="mt-6 font-heading font-bold text-[18px] md:text-[20px] text-pv-ink leading-tight">
                  {r.title}
                </h3>
                <p className="mt-3 text-[14.5px] md:text-[15px] text-slate-600 leading-relaxed">
                  {r.body}
                </p>
              </article>
            );
          })}
        </div>

        <div className="mt-12 md:mt-14 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-6">
          <Link
            to="/bookacall"
            data-testid="pv-home-why-cta-demo"
            onClick={() => setLeadSource('home-why-demo')}
            className="pv-btn-primary group justify-center"
          >
            {c.ctaPrimary}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/perche-sceglierci"
            data-testid="pv-home-why-cta-secondary"
            className="pv-link-quiet justify-center sm:justify-start"
          >
            {c.ctaSecondary}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeWhy;
